import React, { ReactNode, useEffect } from 'react'
import styled from 'styled-components'
import { FormScreen, FormValues } from '../../screen-templates/FormScreen'
import { onBackClickCallback, onNextClickType } from 'components/types'
import * as Yup from 'yup'
import { QuizAnswerKey, Quizzes } from '../../../redux/quiz/types'
import { Slider } from '../fields/Slider'
import { DSSpacer } from '@zoe/ds-web'
import { useFormContext } from 'react-hook-form'

type Question = {
  questionKey: QuizAnswerKey
  question: string
}

export type SliderTagColorScheme =
  | 'gut'
  | 'physical'
  | 'mood'
  | 'energy'
  // e7 ("Symptom score v2") symptom-category tags. Figma uses two design-system
  // tag variants: "brand" (taupe) for gut/appetite and "decoration" for the rest.
  | 'gut_digestion'
  | 'inflammation'
  | 'energy_mood'
  | 'appetite_weight'
  | 'hormonal'

export type SliderTag = {
  label: string
  colorScheme: SliderTagColorScheme
}

const TAG_COLOURS: Record<SliderTagColorScheme, { background: string; color: string }> = {
  gut: { background: '#E5D9C8', color: '#1B1B1F' },
  physical: { background: '#D1C6CE', color: '#1B1B1F' },
  mood: { background: '#CDD1D8', color: '#1B1B1F' },
  energy: { background: '#CCD9CA', color: '#1B1B1F' },
  // e7 symptom-category tag colours (from Figma)
  gut_digestion: { background: '#E5D9C8', color: '#2D3246' },
  inflammation: { background: '#E6D6D1', color: '#2D3246' },
  energy_mood: { background: '#CCD9CA', color: '#2D3246' },
  appetite_weight: { background: '#D1CFD9', color: '#2D3246' },
  hormonal: { background: '#E6D6D1', color: '#2D3246' },
}

const TagPill = styled.span<{ $background: string; $color: string }>`
  display: inline-block;
  padding: 10px 14px;
  border-radius: 18px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  font-size: 16px;
  font-weight: 500;
  line-height: 1;
`

const SlidersGroup = ({ children }: { children: ReactNode }) => {
  const { formState } = useFormContext()
  const hasErrors = formState.errors && Object.keys(formState.errors).length > 0

  useEffect(() => {
    if (formState.submitCount > 0 && hasErrors) {
      const keys = Object.keys(formState.errors)
      const firstErrorKey = keys[0]
      const firstErrorElement = document.getElementById(firstErrorKey)
      if (firstErrorElement && firstErrorElement.scrollIntoView) {
        firstErrorElement.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }, [formState.submitCount, hasErrors])
  return <>{children}</>
}

export type MultipleSlidersScreenProps = {
  title: string
  buttonText?: string
  questions: Question[]
  sliderOptionValues: string[]
  labels: string[]
  requiredFieldMissingMessage: string
  tag?: SliderTag
}

type Props = MultipleSlidersScreenProps & {
  onBackClick?: onBackClickCallback
  onNextClick: onNextClickType
  quiz: Quizzes
}

export const MultipleSlidersScreen: React.FC<Props> = ({
  title,
  buttonText,
  questions,
  onNextClick,
  onBackClick,
  sliderOptionValues,
  labels,
  requiredFieldMissingMessage,
  tag,
}) => {
  // Always render sliders in their untouched state — every visit asks the
  // user to make a fresh selection rather than carrying a previous answer
  // back into the form on revisit.
  const initialValues: FormValues = Object.fromEntries(questions.map((q) => [q.questionKey, '']))
  const formBody = questions.map((question, index) => {
    return (
      <div key={index} id={question.questionKey}>
        <Slider
          name={question.questionKey}
          values={sliderOptionValues}
          label={question.question}
          sliderLabels={labels}
        />
        <DSSpacer size={12} direction="vertical" />
      </div>
    )
  })
  const validationSchemaEntries = questions.map((question) => [
    question.questionKey,
    Yup.string().required(requiredFieldMissingMessage),
  ])

  const tagNode = tag ? (
    <TagPill $background={TAG_COLOURS[tag.colorScheme].background} $color={TAG_COLOURS[tag.colorScheme].color}>
      {tag.label}
    </TagPill>
  ) : null

  return (
    <FormScreen
      aboveTitle={tagNode}
      screenTitle={title}
      titleVariant="fluid-heading-600"
      buttonText={buttonText ?? 'Continue'}
      // Keep the Continue button enabled even when sliders are untouched, so
      // pressing it surfaces validation errors (and the auto-scroll to the
      // first error in SlidersGroup) instead of silently doing nothing.
      forceEnableButton={true}
      valueMapper={(values) => {
        const map = sliderOptionValues
        const entries = Object.entries(values)
        const newEntries = entries.map(([key, value]) => [key, map[parseInt(value)]])
        return Object.fromEntries(newEntries)
      }}
      form={{
        body: <SlidersGroup>{formBody}</SlidersGroup>,
        initialValues,
        validationSchema: Yup.object(Object.fromEntries(validationSchemaEntries)),
      }}
      onNextClick={onNextClick}
      onBackClick={onBackClick}
    />
  )
}
